import {
  FieldValidationLevel,
  NumericField,
  QuoteStatusEnum,
  SideEnum,
  Unit,
  getScaleFromIncrement,
  toBigWithDefault,
  type Currency,
  type FieldValidationRule,
  type Quote,
  type QuoteMarket,
} from '@talos/kyoko';
import Big from 'big.js';

export const RFQSweeperFillExecutionStatus = {
  Staged: 'staged',
  Pending: 'pending',
  Filled: 'filled',
  Error: 'error',
} as const;
export type RFQSweeperFillExecutionStatusValue =
  (typeof RFQSweeperFillExecutionStatus)[keyof typeof RFQSweeperFillExecutionStatus];

export interface RFQSweeperFillExecutionState {
  status: RFQSweeperFillExecutionStatusValue;
  tooltip?: string;
}

/**
 * A selectable sweep quote.
 *
 * Quote sweeping is fed by quote rows where each row contributes its first market as the
 * executable market. Keep the parent quote and market bundled for better devx.
 */
export interface RFQSweeperQuote {
  quote: Quote;
  quoteMarket: QuoteMarket;
}

export interface RFQSweeperFill {
  /** Quantity currently available on this market for the sweeper side. */
  availableQty: Big;
  /** Executable price for this market on the sweeper side. */
  px: string;
  /** User-editable quantity for this market. Field errors are suitable for input tooltips. */
  desiredQty: NumericField;
  executionStatus: RFQSweeperFillExecutionState;
  quote: RFQSweeperQuote;
}

interface RFQSweeperState {
  quotes: RFQSweeperQuote[];
  fills: RFQSweeperFill[];
  side: SideEnum;
  currency: Currency | undefined;
  orderQty: string | undefined;
}

interface RFQSweeperFillValidationContext {
  fill: RFQSweeperFill;
}

interface RFQSweeperCreateFromQuotesParams {
  quotes: Quote[];
  side: SideEnum;
  currency: Currency | undefined;
}

interface RFQSweeperCreateParams {
  quotes: RFQSweeperQuote[];
  side: SideEnum;
  currency: Currency | undefined;
}

/**
 * Immutable helper for staging an RFQ sweep across multiple quote markets.
 *
 * Every mutating action returns a new RFQSweeper instance. `fills` contains the selected
 * markets to trade, and each fill owns a `NumericField` so UI inputs can use the same
 * validation state and `errorString` tooltip text as the rest of OMS.
 *
 * The sweeper is intentionally state-only. It exposes small transition methods for UI
 * updates and execution status changes, while `executeRFQSweeper` performs websocket/API
 * side effects and feeds updated sweeper instances back through an `onUpdate` callback.
 *
 * RFQ sweeping receives one `RFQSweeperQuote` per selectable market. Metadata shared by
 * the basket, like total `OrderQty`, comes from the first quote. Per-market execution
 * metadata, like RFQID and QuoteID, comes from the selected fill's bundled quote.
 */
export class RFQSweeper {
  public readonly quotes: RFQSweeperQuote[];
  public readonly fills: RFQSweeperFill[];
  public readonly side: SideEnum;
  public readonly currency: Currency | undefined;
  public readonly orderQty: string | undefined;

  private constructor({ quotes, fills, side, currency, orderQty }: RFQSweeperState) {
    this.quotes = [...quotes];
    this.fills = fills.map(fill => ({ ...fill }));
    this.side = side;
    this.currency = currency;
    this.orderQty = orderQty;
  }

  public static createFromQuotes({ quotes, side, currency }: RFQSweeperCreateFromQuotesParams): RFQSweeper {
    return RFQSweeper.create({
      quotes: getSweeperQuotesFromQuotes(quotes),
      side,
      currency,
    });
  }

  /** Creates a sweeper from already-paired quotes, preserving quote/market ownership. */
  public static create({ quotes, side, currency }: RFQSweeperCreateParams): RFQSweeper {
    return new RFQSweeper({
      quotes,
      fills: [],
      side,
      currency,
      orderQty: quotes.at(0)?.quote.OrderQty,
    });
  }

  /** Recreates the sweeper with a new side so side-derived fill fields cannot leak across toggles. */
  public updateSide(side: SideEnum): RFQSweeper {
    if (side === this.side) {
      return this;
    }

    return RFQSweeper.create({
      quotes: this.quotes,
      side,
      currency: this.currency,
    });
  }

  /** Selects a market for sweeping and defaults desired quantity to that market's available quantity. */
  public addMarket(marketName: string): RFQSweeper {
    if (this.fills.some(fill => getMarketName(fill.quote) === marketName)) {
      return this;
    }

    const quote = findSweeperQuote(this.quotes, marketName);
    if (!quote) {
      return this;
    }

    const market = quote.quoteMarket;
    const availableQty = this.getAvailableQty(market);
    const fill = this.validateFill({
      availableQty,
      px: this.getPx(market),
      desiredQty: this.createDesiredQtyField(market.Market, availableQty),
      executionStatus: { status: RFQSweeperFillExecutionStatus.Staged },
      quote,
    });

    return this.withState({
      fills: [...this.fills, fill],
    });
  }

  /** Removes a selected market by `QuoteMarket.Market`. */
  public removeMarket(marketName: string): RFQSweeper {
    const fills = this.fills.filter(fill => getMarketName(fill.quote) !== marketName);
    return this.withState({
      fills,
    });
  }

  /** Updates the user-editable desired quantity for a selected market. */
  public setDesiredQty(marketName: string, qty: string): RFQSweeper {
    return this.withState({
      fills: this.fills.map(fill => {
        if (getMarketName(fill.quote) !== marketName) {
          return fill;
        }

        return this.validateFill({
          ...fill,
          desiredQty: fill.desiredQty.updateValue(qty),
        });
      }),
    });
  }

  /**
   * Applies fresh quote revisions while preserving selected fills by market.
   *
   * Selected fills keep their desired quantity and execution status when their market is
   * still present. Availability, price, status validation, and parent quote metadata are
   * refreshed from the matching `RFQSweeperQuote`. Selected fills whose market disappears
   * are removed.
   */
  public updateQuotes(quotes: RFQSweeperQuote[]): RFQSweeper {
    const fills = this.fills.flatMap(fill => {
      const quote = findSweeperQuote(quotes, getMarketName(fill.quote));
      if (!quote) {
        // If we get a quote update where the market this fill is referencing no longer exists, we drop the fill
        return [];
      }

      const market = quote.quoteMarket;
      const updatedFill = {
        ...fill,
        availableQty: this.getAvailableQty(market),
        px: this.getPx(market),
        quote,
      };

      return [this.validateFill(updatedFill)];
    });

    return this.withState({
      quotes,
      fills,
      orderQty: quotes.at(0)?.quote.OrderQty,
    });
  }

  /** Sum of all selected desired quantities, even when validation errors block execution. */
  public get totalQty(): string {
    return this.totalQtyBig.toFixed();
  }

  public get loading(): boolean {
    return this.fills.some(fill => fill.executionStatus.status === RFQSweeperFillExecutionStatus.Pending);
  }

  public get sweepDisabled(): boolean {
    return this.loading || !!this.error || !this.hasExecutableFills || this.hasNonExecutableFills;
  }

  /** First validation error across selected fills. */
  public get error(): string | undefined {
    const fillError = this.fills.map(getFillError).find(error => error != null);

    if (fillError) {
      return fillError;
    }

    if (this.orderQty && this.totalQtyBig.gt(toBigWithDefault(this.orderQty, 0))) {
      return `Cumulative desired quantity exceeds total quantity ${this.orderQty}`;
    }

    return undefined;
  }

  public markMarketsPending(marketNames: Iterable<string>): RFQSweeper {
    return this.withFillExecutionStatuses(
      new Map([...marketNames].map(marketName => [marketName, { status: RFQSweeperFillExecutionStatus.Pending }]))
    );
  }

  /** Applies terminal execution results reported by `executeRFQSweeper`. */
  public markMarketsWithExecutionStatus(statusesByMarketName: Map<string, RFQSweeperFillExecutionState>): RFQSweeper {
    return this.withFillExecutionStatuses(statusesByMarketName);
  }

  private get hasExecutableFills(): boolean {
    return this.fills.some(isExecutableFill);
  }

  private get hasNonExecutableFills(): boolean {
    return this.fills.some(fill => !isExecutableFill(fill));
  }

  private get totalQtyBig(): Big {
    return this.fills.reduce((total, fill) => total.plus(toBigWithDefault(fill.desiredQty.value, 0)), Big(0));
  }

  private withFillExecutionStatuses(statusesByMarketName: Map<string, RFQSweeperFillExecutionState>): RFQSweeper {
    return this.withState({
      fills: this.fills.map(fill => ({
        ...fill,
        executionStatus: statusesByMarketName.get(getMarketName(fill.quote)) ?? fill.executionStatus,
      })),
    });
  }

  private withState(state: Partial<RFQSweeperState>): RFQSweeper {
    return new RFQSweeper({
      quotes: this.quotes,
      fills: this.fills,
      side: this.side,
      currency: this.currency,
      orderQty: this.orderQty,
      ...state,
    });
  }

  private createDesiredQtyField(marketName: string, availableQty: Big): NumericField {
    return new NumericField({
      name: `${marketName} qty`,
      isRequired: true,
      unit: Unit.Decimal,
      scale: getCurrencyScale(this.currency),
    }).updateValue(availableQty.toFixed(), true);
  }

  /** Helps you get the qty for a quoteMarket. Looks at side, and handles backward compat around Qty */
  private getAvailableQty(quoteMarket: QuoteMarket): Big {
    if (this.side === SideEnum.Buy) {
      return toBigWithDefault(quoteMarket.OfferQty ?? this.orderQty, 0);
    }

    return toBigWithDefault(quoteMarket.BidQty ?? this.orderQty, 0);
  }

  private getPx(quoteMarket: QuoteMarket): string {
    return this.side === SideEnum.Buy ? quoteMarket.OfferPx : quoteMarket.BidPx;
  }

  private validateFill(fill: RFQSweeperFill): RFQSweeperFill {
    const desiredQty = fill.desiredQty.validate([fillQuantityValidation], { fill });

    return {
      ...fill,
      desiredQty: desiredQty.hasError ? desiredQty.setTouched(true) : desiredQty,
    };
  }
}

/** Finds the paired quote for a market name. Market name is the stable identity in the sweeper UI. */
function findSweeperQuote(quotes: RFQSweeperQuote[], marketName: string): RFQSweeperQuote | undefined {
  return quotes.find(quote => getMarketName(quote) === marketName);
}

/** Converts raw Quote rows into the quote/first-market pair expected by the sweeper. */
function getSweeperQuotesFromQuotes(quotes: Quote[]): RFQSweeperQuote[] {
  return quotes.flatMap(quote => {
    const quoteMarket = quote.Markets.at(0);

    return quoteMarket ? [{ quote, quoteMarket }] : [];
  });
}

/** Centralizes market identity access so paired quote fields do not leak into every caller. */
function getMarketName(quote: RFQSweeperQuote): string {
  return quote.quoteMarket.Market;
}

/** Error fills are executable by design so users can retry only failed markets. */
function isExecutableFill(fill: RFQSweeperFill): boolean {
  return (
    fill.executionStatus.status === RFQSweeperFillExecutionStatus.Staged ||
    fill.executionStatus.status === RFQSweeperFillExecutionStatus.Error // error means that our attempt at executing failed for some reason. The user can try again.
  );
}

/** Returns the user-facing validation message for the first invalid fill field. */
function getFillError(fill: RFQSweeperFill): string | undefined {
  const error = fill.desiredQty.errors.at(0);

  if (!error) {
    return undefined;
  }

  if (typeof error.message === 'string') {
    return error.message;
  }

  return fill.desiredQty.errorString || 'Desired quantity is invalid';
}

// Validation is attached to each fill's NumericField so the blotter input can surface
// the same error string that blocks confirm.
const fillQuantityValidation: FieldValidationRule<NumericField, RFQSweeperFillValidationContext> = (field, context) => {
  if (!context) {
    return null;
  }

  const { fill } = context;
  const market = fill.quote.quoteMarket;
  const marketName = market.Market;

  if (market.QuoteStatus !== QuoteStatusEnum.Open) {
    return {
      message: `${marketName} market is ${market.QuoteStatus}`,
      level: FieldValidationLevel.Error,
    };
  }

  if (field.bigValue?.gt(fill.availableQty)) {
    return {
      message: `${marketName} quantity exceeds available quantity ${fill.availableQty.toFixed()}`,
      level: FieldValidationLevel.Error,
    };
  }

  return null;
};

/** Desired quantity precision follows the RFQ currency; unit RFQs use whole numbers. */
function getCurrencyScale(currency: Currency | undefined): number {
  if (!currency) {
    return 0;
  }

  return getScaleFromIncrement(currency.MinIncrement);
}
